import { useState, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useSettings } from '../settings/SettingsContext';
import { useEntitlements } from '../subscription/offers';
import { useTrial } from '../subscription/trial';
import { useT } from '../i18n/i18n';
import Reversi, { REVERSI_DEFAULTS, ReversiOptionsForm, type ReversiOptions } from './Reversi';
import WordTiles, { WORD_TILES_DEFAULTS, WordTilesOptionsForm, wordsFor, type WordTilesOptions } from './WordTiles';
import './leisure.css';

type GameDef = { id: string; name: string; blurb: string };

const GAMES: GameDef[] = [
  { id: 'reversi', name: 'Reversi', blurb: 'Two players take turns. Place a piece so it closes a line of the other colour, and those pieces turn to yours.' },
  { id: 'word-tiles', name: 'Word Tiles', blurb: 'Drag letters onto the board to make words that join up, like a crossword.' },
];

function useCanPlay(): boolean | null {
  const { subscriptionStatus } = useAuth();
  const entitlements = useEntitlements();
  const { trial } = useTrial();
  if (entitlements === null) return null;
  const paid = subscriptionStatus === 'ACTIVE' || subscriptionStatus === 'TRIALING';
  return paid && entitlements.main && !trial?.limited;
}

function Locked() {
  const t = useT();
  return (
    <div className="card lz-locked" role="note">
      <p>{t('Games can be played with a subscription or during the full trial.')}</p>
      <div className="toolbar">
        <Link className="btn" to="/account/subscription">{t('See plans')}</Link>
      </div>
    </div>
  );
}

function GamesGate({ children }: { children: ReactNode }) {
  const { settings } = useSettings();
  if (!settings.features.games) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function GamesList() {
  const t = useT();
  const canPlay = useCanPlay();
  return (
    <GamesGate>
      <section className="lz-page">
        <h1>{t('Games')}</h1>
        <p className="field-hint">{t('Games to play for free time or as a reward. Choose one to see its options.')}</p>
        {canPlay === false && <Locked />}
        <ul className="lz-list">
          {GAMES.map((g) => (
            <li key={g.id}>
              <Link className="card lz-game-card" to={`/games/${g.id}`}>
                <strong>{t(g.name)}</strong>
                <span className="field-hint">{t(g.blurb)}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </GamesGate>
  );
}

export function GamePlay() {
  const t = useT();
  const { id } = useParams();
  const canPlay = useCanPlay();
  const game = GAMES.find((g) => g.id === id);
  const [reversi, setReversi] = useState<ReversiOptions>(REVERSI_DEFAULTS);
  const [words, setWords] = useState<WordTilesOptions>(WORD_TILES_DEFAULTS);
  const [playing, setPlaying] = useState(0);

  if (!game) return <Navigate to="/games" replace />;
  const blocked = game.id === 'word-tiles' && wordsFor(words).length === 0;

  return (
    <GamesGate>
      <section className="lz-page">
        <p><Link to="/games">{t('All games')}</Link></p>
        <h1>{t(game.name)}</h1>
        {playing > 0 && canPlay ? (
          <>
            {game.id === 'reversi' ? <Reversi key={playing} options={reversi} /> : <WordTiles key={playing} options={words} onAgain={() => setPlaying((k) => k + 1)} />}
            <div className="toolbar lz-actions">
              <button type="button" className="btn secondary" onClick={() => setPlaying(0)}>{t('Change options')}</button>
            </div>
          </>
        ) : (
          <>
            <p className="field-hint">{t(game.blurb)}</p>
            {canPlay === false && <Locked />}
            <form
              className="card lz-options"
              onSubmit={(e) => { e.preventDefault(); if (canPlay && !blocked) setPlaying((n) => n + 1); }}
            >
              <fieldset disabled={!canPlay}>
                <legend className="field-label">{t('Options')}</legend>
                {game.id === 'reversi'
                  ? <ReversiOptionsForm value={reversi} onChange={setReversi} />
                  : <WordTilesOptionsForm value={words} onChange={setWords} />}
              </fieldset>
              <div className="toolbar">
                <button type="submit" className="btn" disabled={!canPlay || blocked}>{t('Continue')}</button>
              </div>
            </form>
          </>
        )}
      </section>
    </GamesGate>
  );
}
